<template>
  <q-page padding>
    <MultiViewLayout :onBackClick="routeToSettings" class="the-change-password">

      <!-- Title -->
      <template v-slot:title>{{ t('settings') }}</template>

      <!-- Heading Text/Content -->
      <template v-slot:heading>
          {{ t('new_passcode_title') }}
      </template>

      <!-- View Unique Content -->
      <template v-slot:default>

        <!-- Old Password -->
        <div class="row the-change-password__old q-pb-md">
          <div class="col" data-testid="oldPassword">
            <zbm-input-with-validation v-model="oldPassword" :type="!showOldPassword ? 'password' : 'text'"
              :rules="[$zb.validators.required()]" :placeholder="hasSimplePatientPasscode ? t('passcode_old') : t('password_old')"
              :inputGroupModifications="$zb.enums.InputGroupModsEnum.onlyBtmBrdr">
              <template v-slot:append>
                <q-icon @click="showOldPassword = !showOldPassword" class="cursor-pointer"
                  :name="showOldPassword ? eyeClosedIcon : eyeOpenedIcon" size="20px" />
              </template>
            </zbm-input-with-validation>
          </div>
        </div>

        <!-- Change/Confirm Password -->
        <div class="row the-change-password__new q-pb-xl">
          <div class="col" data-testid="confirmPassword">
            <PasswordConfirmWithValidation ref="passwordComponent" v-on:validate="syncPasswordResult"
              v-model="passwordResult" :initialPlaceholder="hasSimplePatientPasscode ? 'forgot_passcode_create_new_code' : 'password_create'
      " :confirmationPlaceholder="hasSimplePatientPasscode ? 'activate_confirm_passcode' : 'password_confirm'
      " :hasPin="hasSimplePatientPasscode" />
          </div>
        </div>

        <!-- Continue Button -->
        <div class="row justify-center">
          <div class="col-auto">

            <zbm-btn @click="onContinueClick" class="button-primary" :color="$zb.enums.ButtonStdColorEnum.accent"
              :height="$zb.enums.ButtonHeightEnum.md" data-testid="continueButton">
              {{ t('activate_continue_text') }}
            </zbm-btn>

            <q-dialog ref="passwordChangeModal" v-model="modalShow">
              <q-card>
                <q-card-section>
                  <div class="row justify-center">
                    <div class="col-auto">
                      <!-- Exclamation Circle Icon -->
                      <q-icon color="dark-teal" name="fa-solid fa-circle-exclamation" size="42px" />
                    </div>
                  </div>
                </q-card-section>
                <q-card-section>
                  <div class="row justify-center">
                    <div class="col text-center">
                      <h5 class="modal-body__heading">{{ t('success') }}</h5>
                      <p>
                        {{ hasSimplePatientPasscode ? t('passcode_confirm_change') : t('password_changed') }}
                      </p>
                    </div>
                  </div>
                </q-card-section>
                <q-card-section>
                  <div class="row justify-center">
                    <div class="col-auto">
                      <zbm-btn @click="props.forcePasswordChange ? router.go(-1) : routeToSettings"
                               :color="$zb.enums.ButtonStdColorEnum.accentText"
                               :height="$zb.enums.ButtonHeightEnum.lg"
                               :label="t('gen_okay')"
                               :data-testid="okayButton" />
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </q-dialog>
          </div>
        </div>
      </template>
    </MultiViewLayout>
  </q-page>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import MultiViewLayout from 'src/components/layouts/MultiViewLayout.vue';
import PasswordConfirmWithValidation from 'src/components/form/PasswordConfirmWithValidation.vue';
import { useSessionStore, useDashboardStore, useLocalizationStore } from 'src/store';
import { ClientFeatureType } from 'src/constants/enums';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { ChangePassword } from 'src/api/endpoints/userWeb';
import { makeEmptyPasswordConfirmWithValidationResult, type PasswordConfirmWithValidationResult } from 'src/utils/componentFactories';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { getUserSession } from 'src/api/endpoints/userWeb';

const { t }= useI18n();

const props = defineProps({
  forcePasswordChange: {
    type: String,
    default: 'false'
  }
})

onMounted(() => {
  showOldPassword.value = false;
  oldPassword.value = '';
  passwordResult.value = makeEmptyPasswordConfirmWithValidationResult();
});

const $q = useQuasar()
const router = useRouter();
const sessionStore = useSessionStore();
const dashboardStore = useDashboardStore();
const localizationStore = useLocalizationStore();
const showOldPassword = ref(false);
const passwordResult = ref(makeEmptyPasswordConfirmWithValidationResult());
const oldPassword = ref('');
const modalShow = ref(false);

const eyeClosedIcon = 'fa-regular fa-eye-slash';
const eyeOpenedIcon = 'fa-regular fa-eye';

const email = computed(() => sessionStore.user?.email || '');
const hasSimplePatientPasscode = computed(() => sessionStore.hasPasscode);

//Returns LoginRequest Type
const loginModel = computed(() => ({
  password: oldPassword.value,
  username: sessionStore.user?.username || '',
  countryCode: sessionStore.user?.clientCountryCode || '',
  utcOffset: sessionStore.utcOffset,
}));

async function onContinueClick() {
  if (passwordResult.value.isValid) {
    const response = await handleApiRequest(() =>
      ChangePassword({
        newPassword: passwordResult.value.password,
        email: email.value,
        loginModel: loginModel.value,
      }), null, $q
    );
    if (response) {
      //Refresh user session to update "password change required" claim value
      if(props.forcePasswordChange === 'true') {
        const userSessionResponse = await getUserSession();
        if(userSessionResponse)
            sessionStore.load(userSessionResponse.user);
      }
        
      modalShow.value = true;
    }
  }
}

function routeToSettings() {
  if (props.forcePasswordChange === 'false')
    router.push({ name: 'Settings' });
}

function syncPasswordResult(result: PasswordConfirmWithValidationResult) {
  passwordResult.value.password = result.password;
  passwordResult.value.confirmPassword = result.confirmPassword;
  passwordResult.value.isValid = result.isValid;
}
</script>
